import React, { useCallback, useEffect, useState } from 'react';
import { adminApiService } from '../../services/adminApiService';
import { useAppSelector } from '../../store/hooks';

export interface ProvisioningRequest {
  id: number;
  referenceCode: string;
  strategyId?: number;
  status: string;
  requiredUsdt: number;
  recommendedDepositUsdt: number;
  reportedDepositUsdt?: number;
  confirmedDepositUsdt?: number;
  depositTxReference?: string;
  subAccountName?: string;
  externalApiConfigId?: number;
  failureReason?: string;
  adminUsername?: string;
  cryptoBlockchain?: string;
  cryptoSymbol?: string;
  cryptoDestinationAddress?: string;
  cryptoTxHash?: string;
  sourceWalletId?: number;
  paymentMethod?: string;
}

interface WalletOption {
  id: number;
  blockchain: string;
  address: string;
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.45)',
  zIndex: 10000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
};

const modalStyle: React.CSSProperties = {
  background: '#fff',
  borderRadius: 12,
  maxWidth: 520,
  width: '100%',
  maxHeight: '90vh',
  overflow: 'auto',
  padding: 24,
  boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
};

const boxStyle: React.CSSProperties = {
  background: '#f9fafb',
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  padding: 16,
};

const btnPrimary: React.CSSProperties = {
  padding: '10px 20px',
  background: '#086afb',
  color: '#fff',
  border: 'none',
  borderRadius: 999,
  fontWeight: 600,
  cursor: 'pointer',
  fontSize: 14,
};

const btnSecondary: React.CSSProperties = {
  padding: '10px 20px',
  background: '#fff',
  color: '#374151',
  border: '1px solid #d1d5db',
  borderRadius: 999,
  fontWeight: 500,
  cursor: 'pointer',
  fontSize: 14,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid #d1d5db',
  borderRadius: 8,
  marginBottom: 12,
  fontSize: 14,
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  color: '#374151',
  marginBottom: 4,
};

const Row: React.FC<{ label: string; value: string; bold?: boolean }> = ({ label, value, bold }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
    <span style={{ color: '#6b7280', fontSize: 13 }}>{label}</span>
    <span style={{ fontWeight: bold ? 700 : 500, fontSize: 14 }}>{value}</span>
  </div>
);

const Alert: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ padding: 12, background: '#fef2f2', border: '1px solid #ff7171', borderRadius: 8, color: '#b91c1c', marginBottom: 12, fontSize: 13 }}>
    {children}
  </div>
);

const linkButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  color: '#086afb',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  textDecoration: 'underline',
  marginBottom: 12,
};

interface CapitalEstimateBreakdown {
  positionSizeUsdt?: number;
  leverage?: number;
  maxOpenPositions?: number;
  marginPerLegUsdt?: number;
  baseMarginPerPairUsdt?: number;
  baseMarginTotalUsdt?: number;
  marginMultiplier?: number;
  safetyBufferMultiplier?: number;
  note?: string;
}

function toNum(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function fmtUsdt(v: unknown, maxFrac = 4): string {
  return toNum(v).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: maxFrac });
}

const CalcStep: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <li style={{ marginBottom: 8, fontSize: 13, color: '#374151', lineHeight: 1.45 }}>{children}</li>
);

const CapitalEstimateCalculationPanel: React.FC<{
  estimate: {
    requiredUsdt?: number;
    recommendedDepositUsdt?: number;
    breakdown?: CapitalEstimateBreakdown;
  };
}> = ({ estimate }) => {
  const b = estimate.breakdown ?? {};
  const order = toNum(b.positionSizeUsdt);
  const lev = Math.max(1, toNum(b.leverage) || 1);
  const maxPos = Math.max(1, toNum(b.maxOpenPositions) || 1);
  const marginPerLeg = toNum(b.marginPerLegUsdt);
  const basePerPair = toNum(b.baseMarginPerPairUsdt);
  const baseTotal = toNum(b.baseMarginTotalUsdt);
  const marginMult = toNum(b.marginMultiplier) || 5;
  const safetyMult = toNum(b.safetyBufferMultiplier) || 1;
  const totalMult = marginMult + safetyMult;
  const required = toNum(estimate.requiredUsdt);
  const recommended = toNum(estimate.recommendedDepositUsdt);

  const marginPerLegFormula =
    lev <= 1
      ? `${fmtUsdt(order, 2)} USDT (full order size at 1× leverage)`
      : `${fmtUsdt(order, 2)} ÷ ${lev} = ${fmtUsdt(marginPerLeg)} USDT per leg`;

  return (
    <div
      style={{
        ...boxStyle,
        marginTop: 8,
        marginBottom: 12,
        background: '#f0f7ff',
        borderColor: '#bfdbfe',
      }}
    >
      <p style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 700, color: '#1e40af' }}>
        How this amount is calculated
      </p>
      <p style={{ margin: '0 0 12px', fontSize: 12, color: '#4b5563' }}>
        Meme-coin pair strategies open a <strong>long and a short</strong> for each open slot. The estimate uses the
        same conservative margin rules as live trading on KuCoin futures.
      </p>
      <ol style={{ margin: 0, paddingLeft: 20 }}>
        <CalcStep>
          <strong>Fixed order amount:</strong> {fmtUsdt(order, 2)} USDT
        </CalcStep>
        <CalcStep>
          <strong>Leverage ({lev}×):</strong> margin per leg = {marginPerLegFormula}
        </CalcStep>
        <CalcStep>
          <strong>Long + short per slot:</strong> {fmtUsdt(marginPerLeg)} × 2 ={' '}
          <strong>{fmtUsdt(basePerPair)} USDT</strong>
        </CalcStep>
        <CalcStep>
          <strong>Max open positions ({maxPos}):</strong> {fmtUsdt(basePerPair)} × {maxPos} ={' '}
          <strong>{fmtUsdt(baseTotal)} USDT</strong> base margin
        </CalcStep>
        <CalcStep>
          <strong>Required USDT:</strong> {fmtUsdt(baseTotal)} × {marginMult} (minimum) + {fmtUsdt(baseTotal)} ×{' '}
          {safetyMult} (safety) = {fmtUsdt(baseTotal)} × {totalMult} = <strong>{fmtUsdt(required, 2)} USDT</strong>
        </CalcStep>
        <CalcStep>
          <strong>Recommended deposit:</strong> {fmtUsdt(required, 2)} × 1.10 (+10% buffer) ={' '}
          <strong>{fmtUsdt(recommended, 2)} USDT</strong>
        </CalcStep>
      </ol>
      {b.note && (
        <p style={{ margin: '12px 0 0', fontSize: 12, color: '#6b7280', fontStyle: 'italic' }}>{b.note}</p>
      )}
    </div>
  );
};

interface WizardProps {
  strategyId?: number;
  fixedOrderAmount?: number;
  leverage?: number;
  maxOpenPositions?: number;
  marketType?: string;
  onClose: () => void;
}

export const KucoinSubaccountProvisioningWizard: React.FC<WizardProps> = ({
  strategyId,
  fixedOrderAmount = 50,
  leverage = 1,
  maxOpenPositions = 1,
  marketType = 'FUTURES',
  onClose,
}) => {
  const token = useAppSelector((state) => state.auth.token) ?? undefined;
  const [step, setStep] = useState<'estimate' | 'deposit' | 'done'>('estimate');
  const [estimate, setEstimate] = useState<any>(null);
  const [request, setRequest] = useState<ProvisioningRequest | null>(null);
  const [masterInfo, setMasterInfo] = useState<any>(null);
  const [reportAmount, setReportAmount] = useState('');
  const [txRef, setTxRef] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [wallets, setWallets] = useState<WalletOption[]>([]);
  const [selectedWalletId, setSelectedWalletId] = useState<number | ''>('');
  const [showCalculationHelp, setShowCalculationHelp] = useState(false);

  const loadMasterInfo = useCallback(async () => {
    const infoRes = await adminApiService.getKucoinMasterDepositInfo(token || undefined);
    if (infoRes.success && infoRes.data) {
      setMasterInfo(infoRes.data);
      const w = infoRes.data.wallets as WalletOption[] | undefined;
      if (Array.isArray(w)) {
        setWallets(w);
        if (w.length === 1) setSelectedWalletId(w[0].id);
      }
    }
  }, [token]);

  const loadEstimate = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const estRes = await adminApiService.estimateStrategyCapital(
        { fixedOrderAmount, leverage, maxOpenPositions, marketType },
        token || undefined
      );
      await loadMasterInfo();
      if (estRes.success && estRes.data) {
        setEstimate(estRes.data);
        setReportAmount(String(estRes.data.recommendedDepositUsdt ?? estRes.data.requiredUsdt ?? ''));
      } else {
        setError(estRes.error || 'Failed to estimate capital');
      }
    } catch (e: any) {
      setError(e.message || 'Failed to load estimate');
    } finally {
      setLoading(false);
    }
  }, [fixedOrderAmount, leverage, maxOpenPositions, marketType, token, loadMasterInfo]);

  useEffect(() => {
    loadEstimate();
  }, [loadEstimate]);

  const createRequest = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApiService.createKucoinSubaccountRequest(
        { strategyId, fixedOrderAmount, leverage, maxOpenPositions, marketType },
        token || undefined
      );
      if (res.success && res.data) {
        setRequest(res.data as ProvisioningRequest);
        await loadMasterInfo();
        setStep('deposit');
      } else {
        setError(res.error || 'Failed to create request');
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const payWithWallet = async () => {
    if (!request) return;
    setLoading(true);
    setError(null);
    try {
      const res = await adminApiService.payKucoinSubaccountWithWallet(
        request.id,
        {
          walletId: selectedWalletId === '' ? undefined : Number(selectedWalletId),
          amount: parseFloat(reportAmount),
        },
        token || undefined
      );
      if (res.success && res.data) {
        setRequest(res.data as ProvisioningRequest);
        setStep('done');
      } else {
        setError(res.error || 'Wallet payment failed');
      }
    } catch (e: any) {
      setError(e.message || 'Wallet payment failed');
    } finally {
      setLoading(false);
    }
  };

  const createPlatformWallet = async () => {
    setLoading(true);
    setError(null);
    try {
      const chain = masterInfo?.blockchain || 'Ethereum';
      const res = await adminApiService.createKucoinSubaccountWallet(chain, token || undefined);
      if (res.success) {
        await loadMasterInfo();
      } else {
        setError(res.error || 'Failed to create wallet');
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const reportDeposit = async () => {
    if (!request) return;
    setLoading(true);
    setError(null);
    try {
      const res = await adminApiService.reportKucoinSubaccountDeposit(
        request.id,
        { amount: parseFloat(reportAmount), txReference: txRef || undefined },
        token || undefined
      );
      if (res.success && res.data) {
        setRequest(res.data as ProvisioningRequest);
        setStep('done');
      } else {
        setError(res.error || 'Failed to report deposit');
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <h2 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 700 }}>Fund new KuCoin sub-account</h2>
        {error && <Alert>{error}</Alert>}

        {step === 'estimate' && (
          <>
            <p style={{ fontSize: 14, color: '#4b5563', marginBottom: 8 }}>
              Required capital is based on order size, leverage, and max open positions.
            </p>
            {estimate && (
              <>
                <button
                  type="button"
                  style={linkButtonStyle}
                  onClick={() => setShowCalculationHelp((v) => !v)}
                  aria-expanded={showCalculationHelp}
                >
                  {showCalculationHelp ? 'Hide calculation details' : 'How is this amount calculated?'}
                </button>
                {showCalculationHelp && <CapitalEstimateCalculationPanel estimate={estimate} />}
                <div style={boxStyle}>
                  <Row label="Required USDT" value={`${estimate.requiredUsdt} USDT`} bold />
                  <Row label="Recommended deposit" value={`${estimate.recommendedDepositUsdt} USDT`} />
                  <p style={{ margin: '10px 0 0', fontSize: 12, color: '#6b7280' }}>
                    Strategy inputs: order {fmtUsdt(fixedOrderAmount, 2)} USDT · leverage {leverage ?? 1}× · max{' '}
                    {maxOpenPositions ?? 1} open position{(maxOpenPositions ?? 1) === 1 ? '' : 's'}
                  </p>
                </div>
              </>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
              <button type="button" onClick={onClose} style={btnSecondary}>Cancel</button>
              <button type="button" onClick={createRequest} disabled={loading || !estimate} style={btnPrimary}>
                {loading ? 'Creating…' : 'Continue'}
              </button>
            </div>
          </>
        )}

        {step === 'deposit' && request && (
          <>
            <div style={boxStyle}>
              <Row label="Reference code" value={request.referenceCode} bold />
              <p style={{ fontSize: 13, color: '#b45309', margin: '8px 0 0' }}>
                Include this reference in your transfer memo when possible.
              </p>
              <Row label="Deposit at least" value={`${request.recommendedDepositUsdt} USDT`} />
              {(request.cryptoDestinationAddress || masterInfo?.destinationAddress) && (
                <Row
                  label="Treasury address"
                  value={request.cryptoDestinationAddress || masterInfo?.destinationAddress}
                />
              )}
              {masterInfo?.destinationAddressError && (
                <p style={{ fontSize: 12, color: '#b91c1c', marginTop: 8 }}>{masterInfo.destinationAddressError}</p>
              )}
              {masterInfo?.blockchain && (
                <Row label="Network" value={`${masterInfo.cryptoSymbol || 'USDT'} on ${masterInfo.blockchain}`} />
              )}
            </div>

            <div style={{ ...boxStyle, marginTop: 12, borderColor: '#086afb' }}>
              <p style={{ fontSize: 14, fontWeight: 600, margin: '0 0 12px', color: '#111827' }}>
                Pay from Zogzila wallet
              </p>
              {wallets.length === 0 ? (
                <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>
                  No wallet on {masterInfo?.blockchain || 'Ethereum'}. Create one below or import via admin Crypto.
                </p>
              ) : (
                <>
                  <label style={labelStyle}>Your wallet</label>
                  <select
                    value={selectedWalletId}
                    onChange={(e) => setSelectedWalletId(e.target.value === '' ? '' : Number(e.target.value))}
                    style={inputStyle}
                  >
                    <option value="">Select wallet…</option>
                    {wallets.map((w) => (
                      <option key={w.id} value={w.id}>
                        {w.blockchain} — {w.address.slice(0, 10)}…{w.address.slice(-6)}
                      </option>
                    ))}
                  </select>
                </>
              )}
              <button
                type="button"
                onClick={createPlatformWallet}
                disabled={loading}
                style={{ ...btnSecondary, marginBottom: 12, width: '100%' }}
              >
                + Create Zogzila wallet on this network
              </button>
              <label style={labelStyle}>Amount ({masterInfo?.cryptoSymbol || 'USDT'})</label>
              <input type="number" value={reportAmount} onChange={(e) => setReportAmount(e.target.value)} style={inputStyle} />
              <button
                type="button"
                onClick={payWithWallet}
                disabled={loading || !reportAmount || wallets.length === 0}
                style={{ ...btnPrimary, width: '100%', marginBottom: 8 }}
              >
                {loading ? 'Sending…' : 'Pay from wallet'}
              </button>
            </div>

            <p style={{ fontSize: 13, color: '#6b7280', margin: '12px 0 8px', textAlign: 'center' }}>
              — or deposit manually outside the app —
            </p>

            <label style={labelStyle}>Amount you sent (USDT)</label>
            <input type="number" value={reportAmount} onChange={(e) => setReportAmount(e.target.value)} style={inputStyle} />
            <label style={labelStyle}>Transaction reference (optional)</label>
            <input type="text" value={txRef} onChange={(e) => setTxRef(e.target.value)} style={inputStyle} />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setStep('estimate')} style={btnSecondary}>Back</button>
              <button type="button" onClick={reportDeposit} disabled={loading || !reportAmount} style={btnSecondary}>
                {loading ? 'Submitting…' : 'I sent deposit manually'}
              </button>
            </div>
          </>
        )}

        {step === 'done' && request && (
          <>
            <div style={{ ...boxStyle, background: '#ecfdf5', borderColor: '#10b981' }}>
              <p style={{ fontSize: 14, color: '#065f46', margin: 0 }}>
                {request.paymentMethod === 'IN_APP_WALLET'
                  ? 'Payment sent from your wallet. A superuser will verify and provision your sub-account.'
                  : 'Deposit reported. A superuser will verify funds, create your sub-account, and transfer USDT.'}
              </p>
              <p style={{ fontSize: 13, color: '#047857', marginTop: 8 }}>
                Status: <strong>{request.status}</strong> · Ref: {request.referenceCode}
              </p>
              {request.cryptoTxHash && (
                <p style={{ fontSize: 12, color: '#047857', marginTop: 6, wordBreak: 'break-all' }}>
                  Tx: {request.cryptoTxHash}
                </p>
              )}
            </div>
            <button type="button" onClick={onClose} style={{ ...btnPrimary, marginTop: 16 }}>Close</button>
          </>
        )}
      </div>
    </div>
  );
};

export const KucoinSubaccountProvisionerPanel: React.FC = () => {
  const token = useAppSelector((state) => state.auth.token) ?? undefined;
  const adminUser = useAppSelector((state) => state.auth.adminUser);
  const canProvision =
    adminUser?.role === 'SUPER_ADMIN' ||
    (adminUser?.permissions?.includes('PROVISION_KUCOIN_SUBACCOUNTS') ?? false);
  const [requests, setRequests] = useState<ProvisioningRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [provisionForm, setProvisionForm] = useState({
    apiKey: '',
    apiSecret: '',
    apiPassphrase: '',
    confirmedAmount: '',
    notes: '',
  });

  const load = useCallback(async () => {
    if (!canProvision) return;
    setLoading(true);
    try {
      const res = await adminApiService.getPendingKucoinSubaccountRequests(token || undefined);
      if (res.success && res.data) setRequests(res.data as ProvisioningRequest[]);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [canProvision, token]);

  useEffect(() => {
    load();
  }, [load]);

  if (!canProvision) {
    return (
      <p style={{ padding: 24, color: '#6b7280' }}>
        You need the PROVISION_KUCOIN_SUBACCOUNTS permission to manage sub-account provisioning.
      </p>
    );
  }

  const selected = requests.find((r) => r.id === selectedId);

  const confirmDeposit = async (id: number) => {
    setError(null);
    const res = await adminApiService.confirmKucoinSubaccountDeposit(
      id,
      {
        confirmedAmount: provisionForm.confirmedAmount ? parseFloat(provisionForm.confirmedAmount) : undefined,
        notes: provisionForm.notes || undefined,
      },
      token || undefined
    );
    if (res.success) await load();
    else setError(res.error || 'Confirm failed');
  };

  const provision = async (id: number) => {
    setError(null);
    const res = await adminApiService.provisionKucoinSubaccount(
      id,
      {
        apiKey: provisionForm.apiKey,
        apiSecret: provisionForm.apiSecret,
        apiPassphrase: provisionForm.apiPassphrase,
      },
      token || undefined
    );
    if (res.success) {
      setSelectedId(null);
      setProvisionForm({ apiKey: '', apiSecret: '', apiPassphrase: '', confirmedAmount: '', notes: '' });
      await load();
    } else {
      setError(res.error || 'Provision failed');
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 16 }}>KuCoin sub-account provisioning</h2>
      {error && <Alert>{error}</Alert>}
      {loading ? (
        <p>Loading…</p>
      ) : requests.length === 0 ? (
        <p style={{ color: '#6b7280' }}>No pending provisioning requests.</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(280px, 1fr) minmax(320px, 1fr)', gap: 16 }}>
          <div>
            {requests.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => {
                  setSelectedId(r.id);
                  setProvisionForm((f) => ({
                    ...f,
                    confirmedAmount: String(r.reportedDepositUsdt ?? r.requiredUsdt ?? ''),
                  }));
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: 12,
                  marginBottom: 8,
                  border: selectedId === r.id ? '2px solid #086afb' : '1px solid #e5e7eb',
                  borderRadius: 8,
                  background: '#fff',
                  cursor: 'pointer',
                }}
              >
                <strong>{r.referenceCode}</strong> — {r.status}
                <br />
                <span style={{ fontSize: 12, color: '#6b7280' }}>
                  {r.adminUsername} · Strategy #{r.strategyId ?? '—'} · Req {r.requiredUsdt} USDT
                </span>
              </button>
            ))}
          </div>
          {selected && (
            <div style={boxStyle}>
              <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>{selected.referenceCode}</h3>
              <Row label="Status" value={selected.status} />
              <Row label="Required" value={`${selected.requiredUsdt} USDT`} />
              <Row label="Reported" value={String(selected.reportedDepositUsdt ?? '—')} />
              {selected.cryptoTxHash && <Row label="On-chain tx" value={selected.cryptoTxHash} />}
              {selected.paymentMethod && <Row label="Payment" value={selected.paymentMethod} />}
              <label style={labelStyle}>Confirmed amount (USDT)</label>
              <input
                type="number"
                value={provisionForm.confirmedAmount}
                onChange={(e) => setProvisionForm({ ...provisionForm, confirmedAmount: e.target.value })}
                style={inputStyle}
              />
              <label style={labelStyle}>Notes</label>
              <input
                type="text"
                value={provisionForm.notes}
                onChange={(e) => setProvisionForm({ ...provisionForm, notes: e.target.value })}
                style={inputStyle}
              />
              {(selected.status === 'DEPOSIT_REPORTED' || selected.status === 'AWAITING_DEPOSIT') && (
                <button type="button" style={{ ...btnPrimary, marginBottom: 16 }} onClick={() => confirmDeposit(selected.id)}>
                  Confirm deposit on master
                </button>
              )}
              {selected.status === 'DEPOSIT_CONFIRMED' && (
                <>
                  <p style={{ fontSize: 13, color: '#4b5563' }}>
                    Create the sub-account in KuCoin, generate API keys (Futures trade, no withdraw), then paste below.
                  </p>
                  <label style={labelStyle}>API Key</label>
                  <input
                    type="password"
                    value={provisionForm.apiKey}
                    onChange={(e) => setProvisionForm({ ...provisionForm, apiKey: e.target.value })}
                    style={inputStyle}
                  />
                  <label style={labelStyle}>API Secret</label>
                  <input
                    type="password"
                    value={provisionForm.apiSecret}
                    onChange={(e) => setProvisionForm({ ...provisionForm, apiSecret: e.target.value })}
                    style={inputStyle}
                  />
                  <label style={labelStyle}>Passphrase</label>
                  <input
                    type="password"
                    value={provisionForm.apiPassphrase}
                    onChange={(e) => setProvisionForm({ ...provisionForm, apiPassphrase: e.target.value })}
                    style={inputStyle}
                  />
                  <button type="button" style={btnPrimary} onClick={() => provision(selected.id)}>
                    Complete provisioning
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
